import { z } from "zod";
import { UuidSchema, TimestampsSchema } from "./common.js";
import { EndpointStatusSchema, PricingModelSchema, EndpointFiltersSchema, } from "./exchange.js";
export const ProviderTypeSchema = z.enum(["vast", "rtb"]);
// A provider can serve multiple protocols; an array expresses any combination
// (e.g. ["vast"], ["rtb"], ["vast", "rtb"]) without a special "all" value.
export const ProviderTypesSchema = z.array(ProviderTypeSchema);
export const ProviderAliasSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Alias may contain only lowercase letters, numbers, and hyphens")
    // "pop" is the popunder callback path segment (/vast/pop/*).
    .regex(/^(?!pop$)/, 'Alias "pop" is reserved');
// Popunder buying on this SSP (an rtb provider). `enabled` is the per-provider
// bidding switch (the only one). A provider
// can only be enabled once its settlement is confirmed first-price with a
// clear-text ${AUCTION_PRICE}: second-price or encrypted prices are not
// onboarded, because the notice price is booked as our cost.
export const POP_DEFAULT_ZONE_FIELDS = [
    "site.ext.idzone",
    "imp.tagid",
    "imp.ext.zone",
    "site.id",
];
const popSettingFields = {
    enabled: z.boolean(),
    // adm shape: a bare redirect URL, or EXADS `<ad><popunderAd>` XML.
    admFormat: z.enum(["url", "exads_xml"]),
    // Where a capped / blocked visit goes; absent ⇒ 204.
    fallbackUrl: z
        .string()
        .max(2000)
        // Printable ASCII only: it ends up in a Location header.
        .regex(/^https?:\/\/[\x21-\x7e]+$/)
        .nullable()
        .optional(),
    // Which callback is the partner's billing event (our cost).
    costOn: z.enum(["burl", "nurl"]),
    // Redirect token lifetime in seconds.
    redirectTtlSec: z.number().int().min(60).max(1800),
    // Send `ext.btype = 1` (CPM) in the bid (EXADS).
    sendBtype: z.boolean(),
    // Notice callers allowed for /n and /b (CIDRs). Empty ⇒ not checked.
    noticeIpAllowlist: z
        .array(z.string().regex(/^[0-9a-fA-F:.]+\/\d{1,3}$/))
        .max(64),
    // On a failed notice write: 503 so the partner retries, or ack with 204.
    noticeFailure: z.enum(["retry", "ack"]),
    // Ordered request paths the zone id is read from; first non-empty wins.
    zoneFields: z
        .array(z.string().regex(/^[a-z]+(\.[A-Za-z_]+){1,3}$/))
        .min(1)
        .max(8),
    // Partner region code → ISO 3166-2 code, applied before normalisation.
    regionMap: z
        .record(z.string().max(16), z.string().regex(/^[A-Z]{2}-[A-Z0-9]{1,3}$/))
        .optional(),
    // No bid when the request carries no `source.ext.schain`.
    requireSchain: z.boolean(),
    // Confirmed at onboarding; `enabled` requires first_price_clear.
    settlement: z.enum(["unconfirmed", "first_price_clear"]),
};
// INPUT shape (inside the HTTP schema): no defaults — Ajv's strict mode refuses
// `default` under the nullable `settings` union. The server fills defaults with
// normalizePopProviderSettings before saving.
export const PopProviderSettingsInputSchema = z
    .object({
    enabled: popSettingFields.enabled.optional(),
    admFormat: popSettingFields.admFormat.optional(),
    fallbackUrl: popSettingFields.fallbackUrl,
    costOn: popSettingFields.costOn.optional(),
    redirectTtlSec: popSettingFields.redirectTtlSec.optional(),
    sendBtype: popSettingFields.sendBtype.optional(),
    noticeIpAllowlist: popSettingFields.noticeIpAllowlist.optional(),
    noticeFailure: popSettingFields.noticeFailure.optional(),
    zoneFields: popSettingFields.zoneFields.optional(),
    regionMap: popSettingFields.regionMap,
    requireSchain: popSettingFields.requireSchain.optional(),
    settlement: popSettingFields.settlement.optional(),
})
    .strict();
// NORMALISED shape: what is stored and what the pop lane reads.
export const PopProviderSettingsSchema = z
    .object({
    enabled: popSettingFields.enabled.default(false),
    admFormat: popSettingFields.admFormat.default("url"),
    fallbackUrl: popSettingFields.fallbackUrl,
    costOn: popSettingFields.costOn.default("burl"),
    redirectTtlSec: popSettingFields.redirectTtlSec.default(300),
    sendBtype: popSettingFields.sendBtype.default(false),
    noticeIpAllowlist: popSettingFields.noticeIpAllowlist.default([]),
    noticeFailure: popSettingFields.noticeFailure.default("retry"),
    zoneFields: popSettingFields.zoneFields.default([
        ...POP_DEFAULT_ZONE_FIELDS,
    ]),
    regionMap: popSettingFields.regionMap,
    requireSchain: popSettingFields.requireSchain.default(false),
    settlement: popSettingFields.settlement.default("unconfirmed"),
})
    .strict();
export const normalizePopProviderSettings = (input) => PopProviderSettingsSchema.parse(input);
// Free-form provider tuning knobs. `transitBufferMs` overrides the default
// transit response buffer for rtb providers that opt into transit serving.
export const ProviderSettingsSchema = z
    .object({
    transitBufferMs: z.number().int().positive().nullable().optional(),
    // When true, the VAST serving path may issue a partitioned `avid` viewer
    // cookie (or fingerprint fallback) to frequency-cap web viewers. Gated
    // additionally by the global WEB_VIEWER_CAPPING_ENABLED env flag; off/unset
    // keeps serving byte-for-byte unchanged.
    issueViewerCookie: z.boolean().optional(),
    // Percent (0..100) of this SSP's RTB bid requests whose full raw OpenRTB
    // body is persisted (adRequestRaw) together with the per-DSP call trace
    // (dspCall). Served/won requests are always captured regardless. Unset ⇒ 1.
    rawSamplePct: z.number().int().min(0).max(100).nullable().optional(),
    // Popunder buying; absent ⇒ the provider takes no pop campaigns.
    pop: PopProviderSettingsInputSchema.nullable().optional(),
})
    .nullable()
    .optional();
// The provider accepts popunder campaigns (configured; bidding may still be
// off). Pop campaigns may only target such providers.
export const providerAcceptsPop = (p) => p.types.includes("rtb") && p.allowTransit !== true && p.settings?.pop != null;
// The provider bids on pop imps right now.
export const isPopProvider = (p) => providerAcceptsPop(p) &&
    p.settings?.pop?.enabled === true &&
    p.settings?.pop?.settlement === "first_price_clear";
// Exchange (SSP endpoint) fields shared by the create/update/response shapes.
// `cpm` (dollars) stays the API-facing fixed buy CPM; the server mirrors it to
// providers.buyCpmMicros for exact arithmetic.
const providerEndpointFields = {
    // Paused ⇒ VAST returns an empty document, RTB returns 204, nothing is routed.
    status: EndpointStatusSchema.optional().default("active"),
    // Max inbound requests/second accepted from this SSP; null ⇒ unlimited.
    maxIncomingQps: z.number().int().positive().nullable().optional(),
    // profit_share (RTB default: floor + 50% of profit) | revshare (RTB: SSP
    // paid share% of DSP bid) | fixed_cpm (VAST: buy CPM).
    pricingModel: PricingModelSchema.optional().default("profit_share"),
    // Revenue share as a percent (0..100, 2 decimals); required for revshare.
    revSharePct: z.number().min(0).max(100).nullable().optional(),
    // Routing filters applied to INBOUND requests from this SSP.
    filters: EndpointFiltersSchema.nullable().optional(),
    // Free-text owning company (no company entity yet).
    companyName: z.string().max(200).nullable().optional(),
};
const refineEndpoint = (data, ctx) => {
    const pop = data.settings?.pop;
    if (pop != null) {
        // `types` may be omitted on a partial update; the route re-checks the
        // merged row. A present `types` without rtb is rejected here.
        if (data.types !== undefined && !data.types.includes("rtb")) {
            ctx.addIssue({
                code: "custom",
                message: "popunder settings require an rtb provider",
                path: ["settings", "pop"],
            });
        }
        if (data.allowTransit === true) {
            ctx.addIssue({
                code: "custom",
                message: "popunder cannot be combined with allowTransit",
                path: ["settings", "pop"],
            });
        }
        if (pop.enabled === true && pop.settlement !== "first_price_clear") {
            ctx.addIssue({
                code: "custom",
                message: "popunder can only be enabled for a partner confirmed first-price with a clear-text price macro",
                path: ["settings", "pop", "settlement"],
            });
        }
    }
    if (data.allowTransit === true && !data.types?.includes("rtb")) {
        ctx.addIssue({
            code: "custom",
            message: "allowTransit requires an rtb provider",
            path: ["allowTransit"],
        });
    }
    if (data.pricingModel === "revshare" && data.revSharePct == null) {
        ctx.addIssue({
            code: "custom",
            message: "revshare pricing requires revSharePct",
            path: ["revSharePct"],
        });
    }
};
export const ProviderBaseSchema = z
    .object({
    uuid: UuidSchema,
    name: z.string(),
    alias: z.string().nullable(),
    types: ProviderTypesSchema,
    secretToken: z.string(),
    kinds: z.number().int().min(0).max(15),
    cpm: z.number(),
    allowTransit: z.boolean().optional().default(false),
    settings: ProviderSettingsSchema,
    ...providerEndpointFields,
})
    .merge(TimestampsSchema);
export const CreateProviderSchema = z
    .object({
    name: z.string().min(1),
    alias: ProviderAliasSchema.nullable().optional(),
    types: ProviderTypesSchema.optional().default(["vast"]),
    kinds: z.number().int().min(0).max(15).optional().default(0),
    cpm: z.number().min(0).optional().default(0),
    allowTransit: z.boolean().optional().default(false),
    settings: ProviderSettingsSchema,
    ...providerEndpointFields,
})
    .superRefine(refineEndpoint);
export const UpdateProviderSchema = z
    .object({
    name: z.string().min(1).optional(),
    alias: ProviderAliasSchema.nullable().optional(),
    types: ProviderTypesSchema.optional(),
    kinds: z.number().int().min(0).max(15).optional(),
    cpm: z.number().min(0).optional(),
    allowTransit: z.boolean().optional().default(false),
    settings: ProviderSettingsSchema,
    status: EndpointStatusSchema.optional(),
    maxIncomingQps: z.number().int().positive().nullable().optional(),
    pricingModel: PricingModelSchema.optional(),
    revSharePct: z.number().min(0).max(100).nullable().optional(),
    filters: EndpointFiltersSchema.nullable().optional(),
    companyName: z.string().max(200).nullable().optional(),
})
    .superRefine(refineEndpoint);
export const ProviderResponseSchema = ProviderBaseSchema;
export const ProviderListResponseSchema = z.array(ProviderResponseSchema);
//# sourceMappingURL=provider.js.map